import { IDropdown } from "../models/input";
import { ITreeNode } from "../models/treeData";
import { API_STATUS_CODES, ERROR_MESSAGES } from "./constant";

// Helper function to get error messages based on status codes
export const getErrorMessageByStatus = (status: number): string => {
    switch (status) {
        case API_STATUS_CODES.BAD_REQUEST:
            return ERROR_MESSAGES.BAD_REQUEST;
        case API_STATUS_CODES.UNAUTHORIZED:
            return ERROR_MESSAGES.UNAUTHORIZED;
        case API_STATUS_CODES.FORBIDDEN:
            return ERROR_MESSAGES.FORBIDDEN;
        case API_STATUS_CODES.NOT_FOUND:
            return ERROR_MESSAGES.NOT_FOUND;
        case API_STATUS_CODES.INTERNAL_SERVER_ERROR:
            return ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
        case API_STATUS_CODES.ACTION_NOT_ALLOWED:
            return ERROR_MESSAGES.ACTION_NOT_ALLOWED;
        default:
            return ERROR_MESSAGES.DEFAULT;
    }
};

// Using reduce and a Map to get unique objects based on `id`
export const uniqueObjects = (array: IDropdown[]): IDropdown[] => {
    return Array.from(
        array.reduce((map, obj) => {
            if (!map.has(obj.id)) {
                map.set(obj.id, obj);
            }
            return map;
        }, new Map<number, IDropdown>()).values()
    );
};

// Recursive function to convert data to tree structure
export const convertToTreeData = (data: any[]): ITreeNode[] => {
    return data.map((node) => {
        const treeNode: any = {
            title: node.name,
            value: node.id.toString(),
            key: node.id.toString(),
        };

        // If the node has children, recursively convert them
        if (node.children && node.children.length > 0) {
            treeNode.children = convertToTreeData(node.children);
        }

        return treeNode;
    });
}

// Recursive function to find top-level parent ids for selected children
export const findTopLevelParents = (data: any[], selectedStringIds: number[]): number[] => {
    const selectedIds: number[] = selectedStringIds.map(Number);
    const topLevelParents = new Set<number>();

    // Function to find a node by its id in the data (traverse entire structure)
    const findNodeById = (data: any[], id: number): any | null => {
        for (const node of data) {
            if (node.id === id) {
                return node;
            }
            if (node.children) {
                const foundNode = findNodeById(node.children, id);
                if (foundNode) {
                    return foundNode;
                }
            }
        }
        return null;
    };

    // Helper function to find the top-level parent of a given node
    const findTopLevelParent = (node: any, data: any[]) => {
        if (node.parent_id === null) {
            topLevelParents.add(node.id);
        } else {
            // Find the parent node by traversing the entire tree structure
            const parentNode = findNodeById(data, node.parent_id);
            if (parentNode) {
                findTopLevelParent(parentNode, data);
            }
        }
    };

    //Function to traverse the tree and check for selected children
    const traverse = (node: any, data: any[]) => {
        // Check if the current node's id is in the selected ids
        if (selectedIds.includes(node.id)) {
            // Find the top-level parent for this node
            findTopLevelParent(node, data);
        }

        // Traverse children recursively
        if (node.children) {
            node.children.forEach((child: any) => traverse(child, data));
        }
    };

    // Start traversal from top-level nodes
    data.forEach((node) => traverse(node, data));

    // Return the unique set as an array
    return Array.from(topLevelParents);
};